"use client";

// Ported from relay-protocol packages/relay-widget/src/components/QuotePreview.tsx
// (v0.1.0). Mechanical substitution only: CSS Modules `styles.x` references
// become literal "rd-x" class names (see ../styles/component.css's header
// mapping table), the CSS Modules import becomes the plain side-effect import
// below, and viem's `formatUnits` is replaced by ../lib/units's
// self-implemented equivalent (dapp-ui may depend on neither wagmi nor viem).
// Everything else -- props and layout -- is the original logic, untouched.
//
// Purely presentational -- receives the live quote (and its loading/error
// state) from <RelayDeposit>, which owns the useDepositAddress call. In the
// address-first flow (Task 4) this preview is entirely optional: the
// deposit address never waits on it, so callers only render this section
// once an amount has actually been entered.
//
// 2026-07-30 redesign ("variant A"), presentation only -- same name, same
// props, same states: the "2. Quote preview" CARD became an accent-tinted STRIP
// that <DepositPanel> renders directly under the amount input, so "send this ->
// get that" reads as feedback on the number just typed. (It sat above a
// "Continue" button until the 2026-07-31 single-view collapse; the strip's own
// position and every state below are unchanged.)
// Every state the card had survives, re-cut to fit:
//   loading  -> a spinner line inside the strip's slot
//   error    -> the danger callout, in place of the strip. Purely informational:
//               a broken quote never hides or blocks the deposit address, which
//               the quote endpoint has no part in minting.
//   belowMin -> the same warning callout, under the strip
//   parity / fee / ETA -> one muted `.rd-quote-strip-hint` line under the strip
//   no quote yet -> the same muted "Enter an amount to see a quote." line
import { formatUnits } from "../lib/units";
import type { Destination, QuoteResult } from "../lib/types";
import "../styles/component.css";

export interface QuotePreviewProps {
  quote?: QuoteResult;
  isLoading: boolean;
  error?: Error;
  humanAmount: string;
  originSymbol?: string;
  destination: Destination;
}

/** Formats an API-supplied base-unit amount, or "--" if it is not a valid integer.
 *
 * BigInt() THROWS on anything that is not an integer string -- "", "1.5", null
 * coerced to "null" -- and this call sits inline in JSX, so an unhandled throw
 * during render unmounts the entire widget tree: one malformed field in a quote
 * response would blank the page rather than degrade one line of it (upstream
 * 01a1387). Every other formatUnits call site in this feature already guards. */
function formatQuoteAmount(raw: string | undefined, decimals: number): string {
  if (!raw) return "--";
  try {
    return formatUnits(BigInt(raw), decimals);
  } catch {
    return "--";
  }
}

export function QuotePreview({ quote, isLoading, error, humanAmount, originSymbol, destination }: QuotePreviewProps) {
  return (
    <div className="rd-stack" data-rd-quote-preview="">
      {/* Same strip container as the loaded state, so the preview area holds
          its height instead of jumping when the quote lands. */}
      {isLoading && (
        <div className="rd-quote-strip" data-testid="rd-quote-skeleton">
          <span className="rd-skeleton rd-skeleton-line" style={{ width: "30%" }} aria-hidden="true" />
          <span className="rd-quote-strip-arrow" aria-hidden="true">
            →
          </span>
          <span className="rd-skeleton rd-skeleton-line" style={{ width: "30%" }} aria-hidden="true" />
        </div>
      )}

      {!isLoading && error && (
        <div className="rd-callout rd-callout-danger">Couldn&apos;t fetch a quote: {error.message}</div>
      )}

      {!isLoading && !error && quote && originSymbol && (
        <>
          <div className="rd-quote-strip">
            <span className="rd-quote-strip-side">
              <b>
                {humanAmount} {originSymbol}
              </b>
              <small>You send</small>
            </span>
            <span className="rd-quote-strip-arrow" aria-hidden="true">
              →
            </span>
            <span className="rd-quote-strip-side rd-quote-strip-end">
              <b>
                ~{formatQuoteAmount(quote.expectedCrossd, destination.decimals)} {destination.symbol}
              </b>
              <small>You receive</small>
            </span>
          </div>

          {quote.belowMin && (
            <div className="rd-callout rd-callout-warning">
              This amount is below the minimum the bridge will accept. Increase the amount to continue.
            </div>
          )}

          {/* Named for what it is (upstream af35dd5): the bridge fee taken out
              of the amount, which is what makes the "receive ~" figure above
              smaller than what was sent. Says nothing about the user's own
              origin gas -- that is a separate wallet charge quoted at signing
              time, and `feeUsd` deliberately excludes it (see
              QuoteResult.feeUsd).

              Rendered only when present: an absent fee is UNKNOWN, and printing
              "$0.00" for a figure nobody could compute would claim the route is
              free. */}
          {(quote.assumesParity || quote.feeUsd) && (
            <p className="rd-quote-strip-hint">
              {quote.feeUsd && (
                <>
                  Bridge fee: ${quote.feeUsd}, already deducted from the amount above. You also pay your own
                  network fee to send the deposit.{" "}
                </>
              )}
              {quote.assumesParity && (
                <>
                  Assumes 1:1 parity between bridged USDT and {destination.symbol}; the final amount may differ
                  slightly.
                </>
              )}
            </p>
          )}
        </>
      )}

      {!isLoading && !error && !quote && <p className="rd-muted">Enter an amount to see a quote.</p>}
    </div>
  );
}
